import * as React from 'react';
import {useEffect} from 'react';
import {Routes, Route} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import AssetsPreviewModal from '@contentlab/components/assets-preview-modal';
import ErrorToast from '@contentlab/components/error-toast/ErrorToast';
import OnboardingUploadModal from '@contentlab/components/feature-editor/components/onboarding-upload-modal';
import {ROUTES} from '@contentlab/config/routesConstants';
import ContentLabOnboardingGuard from '@contentlab/routes/contentlab/ContentLabOnboardingGuard';
import contentLabStore from '@contentlab/stores/contentLabStore';
import ContentLab from '@contentlab/views/contentlab/ContentLab';
import AiAvatarPage from '@contentlab/views/contentlab/pages/ai-avatar-page';
import CaptionsPage from '@contentlab/views/contentlab/pages/captions-page';
import DubPage from '@contentlab/views/contentlab/pages/dub-page';
import Home from '@contentlab/views/contentlab/pages/home';
import OnboardingWizard from '@contentlab/views/contentlab/pages/onboarding-page/OnboardingWizard';
import styles from '@contentlab/views/contentlab/pages/onboarding-page/onboardingWizard.module.scss';
import ProjectPage from '@contentlab/views/contentlab/pages/project-page';
import ProjectsPage from '@contentlab/views/contentlab/pages/projects-page';
import RephrasePage from '@contentlab/views/contentlab/pages/rephrase-page';
import ResizePage from '@contentlab/views/contentlab/pages/resize-page';
import UpscalePage from '@contentlab/views/contentlab/pages/upscale-page';
import ContentLabPricingPage from '@contentlab/views/contentlab/pricing';
import FreeTrialEndModal from '@contentlab/views/contentlab/pricing/components/free-trial-end-modal/FreeTrialEndModal';
import PreferenceRoutes from '@/routes/preferences';
import useNavigation from '@/hooks/use-navigation';
import useSetBrandIdFromLoggedInDashboardUser from '@/hooks/use-set-brand-id-from-logged-in-dashboard-user';

function AssetsRedirect() {
  const {navigate} = useNavigation();
  useEffect(() => {
    navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECTS}`);
  }, []);
  return null;
}

export type ContentLabRoutesProps = {
  flags: any;
};

function ContentLabRoutes(props: ContentLabRoutesProps) {
  const {onboardingState} = contentLabStore(
    useShallow((state) => ({
      onboardingState: state.onboardingState,
    }))
  );

  useSetBrandIdFromLoggedInDashboardUser();

  const {flags} = props;

  return (
    <>
      <Routes>
        <Route element={<ContentLabOnboardingGuard />}>
          <Route element={<ContentLab />}>
            <Route index element={<Home />} />
            <Route path="/assets" element={<AssetsRedirect />} />
            <Route
              path={`${ROUTES.CONTENT_LAB_PREFERENCES}/*`}
              element={
                <PreferenceRoutes
                  basePath={`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PREFERENCES}`}
                />
              }
            />
            <Route
              path={ROUTES.CONTENT_LAB_PROJECTS}
              element={<ProjectsPage />}
            />
            <Route
              path={`${ROUTES.CONTENT_LAB_PROJECT}/:projectId`}
              element={<ProjectPage />}
            />
            <Route path={ROUTES.CONTENT_LAB_RESIZE} element={<ResizePage />} />
            <Route path={ROUTES.CONTENT_LAB_DUB} element={<DubPage />} />
            <Route
              path={ROUTES.CONTENT_LAB_REPHRASE}
              element={<RephrasePage />}
            />
            {flags.clCaptions && (
              <Route
                path={ROUTES.CONTENT_LAB_CAPTIONS}
                element={<CaptionsPage />}
              />
            )}
            {flags.clAiAvatar && (
              <Route
                path={ROUTES.CONTENT_LAB_AI_AVATAR}
                element={<AiAvatarPage />}
              />
            )}
            {flags.clUpscale && (
              <Route
                path={ROUTES.CONTENT_LAB_UPSCALE}
                element={<UpscalePage />}
              />
            )}
          </Route>
          <Route
            path={ROUTES.CONTENT_LAB_PRICING}
            element={<ContentLabPricingPage flags={flags} />}
          />
          <Route
            path={ROUTES.CONTENT_LAB_ONBOARDING}
            element={
              <div className={styles.parentContainer}>
                <OnboardingWizard />
              </div>
            }
          />
        </Route>
      </Routes>
      {onboardingState.targetFeature && (
        <OnboardingUploadModal
          feature={onboardingState.targetFeature}
          isOnboardingModalOpen={onboardingState.isOnboardingModalOpen}
        />
      )}
      <AssetsPreviewModal />
      <FreeTrialEndModal />
      <ErrorToast />
    </>
  );
}

export default ContentLabRoutes;
